import type { FC } from 'react';
import React, { useCallback } from 'react';
import { Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { Edit, LockFilled, Moon, PaintBucket, Photos, Sun, Fonts, Video } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { EventPageVisibility } from '@graphql/generated-base';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

export type ActionData = Partial<{
  [key in InlineEditorActions]: key extends InlineEditorActions.VISIBILITY ? { visibility: EventPageVisibility; function: () => void } : () => void;
}>;

export enum InlineEditorActions {
  EDIT = 'edit',
  FONT = 'font',
  VISIBILITY = 'visibility',
  EDIT_PHOTO = 'editPhoto',
  EDIT_THEME_COLOR = 'editThemeColor',
  ADD_PHOTO = 'addPhoto',
  EDIT_MONOGRAM = 'editMonogram',
  EDIT_VIDEO = 'editVideo',
}

type ActionBarProps = {
  actionData: ActionData;
  actionView?: 'compact' | 'detailed';
  scaleValue: number;
  onActionClick: (action: InlineEditorActions) => void;
};

export const PAGE_VISIBILITY_ICON_MAPPER = {
  [EventPageVisibility.public]: Sun,
  [EventPageVisibility.passwordProtected]: LockFilled,
  [EventPageVisibility.hidden]: Moon,
};

const ACTION_TO_ICON_MAPPER = (visibility?: EventPageVisibility) => ({
  [InlineEditorActions.EDIT]: Edit,
  [InlineEditorActions.FONT]: Fonts,
  [InlineEditorActions.VISIBILITY]: PAGE_VISIBILITY_ICON_MAPPER[visibility || EventPageVisibility.public],
  [InlineEditorActions.EDIT_PHOTO]: Photos,
  [InlineEditorActions.EDIT_MONOGRAM]: Photos,
  [InlineEditorActions.EDIT_THEME_COLOR]: PaintBucket,
  [InlineEditorActions.ADD_PHOTO]: Photos,
  [InlineEditorActions.EDIT_VIDEO]: Video,
});

export const ActionBar: FC<ActionBarProps> = props => {
  const { actionData, actionView = 'compact', scaleValue, onActionClick } = props;

  const { tq } = useTranslation('joykit');

  const labelTexts = useEventCallback((visibility?: EventPageVisibility) => {
    const visbiblityTexts = {
      [EventPageVisibility.public]: tq`This page is public.`,
      [EventPageVisibility.passwordProtected]: tq`This page is password protected.`,
      [EventPageVisibility.hidden]: tq`This page is hidden.`,
    };
    return {
      [InlineEditorActions.EDIT]: tq`Content`,
      [InlineEditorActions.FONT]: tq`Font Styles`,
      [InlineEditorActions.VISIBILITY]: visbiblityTexts[visibility || EventPageVisibility.public],
      [InlineEditorActions.EDIT_PHOTO]: tq`Edit Photo`,
      [InlineEditorActions.EDIT_MONOGRAM]: tq`Edit Monogram`,
      [InlineEditorActions.EDIT_THEME_COLOR]: tq`Edit Color`,
      [InlineEditorActions.ADD_PHOTO]: tq`Add Page Photo`,
      [InlineEditorActions.EDIT_VIDEO]: tq`Edit Video`,
    };
  });

  const generatedActionList = Object.keys(actionData).map(actionName => ({
    id: actionName as InlineEditorActions,
    icon: ACTION_TO_ICON_MAPPER(actionData.visibility?.visibility)[actionName as InlineEditorActions],
    onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
      onActionClick(actionName as InlineEditorActions);
      const action = actionData[actionName as InlineEditorActions];
      if (action) {
        if (typeof action === 'function') {
          action(); // Call if it's a simple function
        } else if (typeof action === 'object' && action.function) {
          action.function(); // Handle visibility specific case
        }
      }
    },
  }));

  const applyScaling = useCallback((value: number) => value / scaleValue, [scaleValue]);

  return (
    <Flex
      position="relative"
      flexDirection="column"
      alignItems="center"
      gap={pxToRem(applyScaling(10))}
      zIndex="overlay"
    >
      <Flex
        flexDirection={actionView === 'compact' ? 'row' : 'column'}
        alignItems={actionView === 'compact' ? 'flex-start' : 'center'}
        gap={actionView === 'compact' ? pxToRem(applyScaling(4)) : pxToRem(applyScaling(16))}
        {...(actionView === 'compact' && {
          padding: pxToRem(applyScaling(8)),
          borderRadius: pxToRem(applyScaling(56)),
          backgroundColor: 'white',
          boxShadow: '0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)',
        })}
        color="mono14"
        pointerEvents="all"
      >
        {generatedActionList.map((action, index) => {
          const Icon = action.icon;
          return actionView === 'compact' ? (
            <TooltipV2
              key={index}
              onMouseEnterDelay={200}
              overrides={{
                Content: {
                  props: {
                    // Size the tooltip to its content so short labels ("Content") don't wrap,
                    // while still capping width so long labels ("This page is password protected.") wrap cleanly.
                    width: 'max-content',
                    maxWidth: pxToRem(applyScaling(200)),
                  },
                },
              }}
              content={
                <TextV2
                  typographyVariant="label2"
                  fontSize={pxToRem(applyScaling(13))}
                  textAlign="center"
                  color="mono4"
                >
                  {labelTexts(actionData.visibility?.visibility)[action.id]}
                </TextV2>
              }
              placement="top"
            >
              <Flex
                width={pxToRem(applyScaling(40))}
                height={pxToRem(applyScaling(40))}
                paddingY={pxToRem(applyScaling(8))}
                alignItems="center"
                justifyContent="center"
                borderRadius={pxToRem(applyScaling(20))}
                _hover={{
                  backgroundColor: 'mono2',
                  color: '#4E06F2',
                }}
                _active={{
                  backgroundColor: 'mono3',
                  color: '#300495',
                }}
                cursor="pointer"
                onClick={action.onClick}
                __css={{ '& > svg': { width: pxToRem(applyScaling(16)), height: pxToRem(applyScaling(16)) } }}
              >
                <Icon size={16} />
              </Flex>
            </TooltipV2>
          ) : (
            <Flex
              key={index}
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              height={pxToRem(applyScaling(56))}
              cursor="pointer"
              gap={pxToRem(applyScaling(12))}
              paddingX={pxToRem(applyScaling(32))}
              borderRadius={pxToRem(applyScaling(56))}
              boxShadow="0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)"
              backgroundColor="white"
              color="mono14"
              _hover={{
                backgroundColor: 'mono2',
                color: '#4E06F2',
              }}
              _active={{
                backgroundColor: 'mono3',
                color: '#300495',
              }}
              onClick={action.onClick}
              __css={{ '& > svg': { width: pxToRem(applyScaling(24)), height: pxToRem(applyScaling(24)) } }}
            >
              <Icon size={24} />
              <TextV2
                typographyVariant="button2"
                fontSize={pxToRem(applyScaling(16))}
                fontFamily="Inter UI"
                fontWeight={600}
                whiteSpace="nowrap"
              >
                {labelTexts(actionData.visibility?.visibility)[action.id]}
              </TextV2>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};

ActionBar.displayName = 'ActionBar';
